import type { ActionFunction } from '~/types/route-args';
import type {
    ModuleColumnInput,
    UpdateModuleMutationVariables,
} from 'generated/graphql';
import { updateModule } from '@modules/page-builder/api/update-module.server';
import { updateModuleData } from '~/routes/remix-api.page-builder.validation.update-module-data-validation';
import { UserErrorResponse } from '@modules/root/graphql/responses';
import { zfd } from 'zod-form-data';

/** Remix action path for updating page builder module / column data. */
export const PAGE_BUILDER_UPDATE_MODULE_DATA_ROUTE =
    '/remix-api/page-builder/update-module-data';

export type PageBuilderUpdateModuleDataAction = typeof action;

export const action: ActionFunction = async ({ request, context }) => {
    const updateModuleDataValidation = zfd.formData(updateModuleData);
    const result = updateModuleDataValidation.safeParse(
        await request.formData(),
    );

    if (!result.success) {
        throw new UserErrorResponse(result.error);
    }

    const data = result.data;
    const variables: UpdateModuleMutationVariables = {
        ParentID: data.parentId,
        ParentClass: data.parentClass,
    };

    if (data.moduleId) {
        variables.id = data.moduleId;
    }

    if (data.presetId) {
        variables.presetID = data.presetId;
    }

    if (data.memberSortOrder !== undefined) {
        variables.memberSortOrder = data.memberSortOrder;
    }

    const columnData = data.columnData;
    const files = data.columnFile;

    if (columnData && columnData.length) {
        variables.columns = columnData.map((col, index) => {
            const column: ModuleColumnInput = {};
            if (files && files[index]) {
                column.File = files[index];
            }
            if (col.id) {
                column.ID = col.id;
            }
            if (col.memberSortOrder !== undefined) {
                column.MemberSortOrder = col.memberSortOrder;
            }
            if (col.textType) {
                column.TextType = col.textType;
            }
            if (col.readonly) {
                column.ReadOnly = col.readonly;
            }
            if ('content' in col) {
                column.Content = col.content;
            }
            if (col.dimension) {
                column.UpdateDimensionID = col.dimension.id;
            }
            if (col.video?.caption) {
                column.Caption = col.video?.caption;
            }
            if (col.video?.videoURL) {
                column.VideoLink = col.video?.videoURL;
            }
            if (col.productId) {
                column.ProductID = col.productId;
            }
            // 'in' check so an empty value clears the stored field.
            if ('fullName' in col) {
                column.FullName = col.fullName ?? '';
            }
            if (col.showFullName !== undefined) {
                column.ShowFullName = col.showFullName;
            }
            if (col.showCtaButtons !== undefined) {
                column.ShowCtaButtons = col.showCtaButtons;
            }
            if (col.alignRight !== undefined) {
                column.AlignRight = col.alignRight;
            }
            // Pass NeedsUpscale to backend - backend will set HideOnPage=true when NeedsUpscale is true
            if (col.needsUpscale) {
                column.NeedsUpscale = col.needsUpscale;
            }
            if (col.libraryImageId) {
                column.LibraryImageID = col.libraryImageId;
            }
            return column;
        });
    }

    return await updateModule({
        ...context,
        variables,
    });
};
